import React, { useState, useRef, useEffect } from 'react';
import { TextField, MenuItem, Select, FormControl, InputLabel, InputAdornment } from '@material-ui/core';
import { MailOutline as EmailIcon } from '@material-ui/icons';
import DateRangeRoundedIcon from '@material-ui/icons/DateRangeRounded';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles, withStyles } from '@material-ui/core';
import { designTokens } from '../../styles/designTokens';
import Button from '../Button';
import PhoneNumberInput from '../PhoneNumberInput';

const useStyles = makeStyles((theme) => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    title: {
        ...designTokens.typography.h1,
        marginBottom: '8px',
        marginTop: '0',
        '@media (max-width: 600px)': {
            fontSize: '20px',
        }
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        '@media (max-width: 600px)': {
            gridTemplateColumns: '1fr',
        }
    },
    fieldWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    selectWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    select: {
        backgroundColor: designTokens.colors.background.card,
        borderRadius: designTokens.borderRadius.button,
        border: `1px solid ${designTokens.colors.border}`,
        '& fieldset': {
            border: 'none !important',
        },
        '&:hover': {
            borderColor: designTokens.colors.grey[300],
        },
        '&:hover fieldset': {
            border: 'none !important',
        },
        '&.Mui-focused': {
            borderColor: `${designTokens.colors.primaryLight} !important`,
        },
        '&.Mui-focused fieldset': {
            border: 'none !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
        },
        '&.Mui-error': {
            borderColor: `${designTokens.colors.red.main} !important`,
        },
    },
    selectInput: {
        padding: '19px 16px 16px !important',
        backgroundColor: 'transparent !important',
        borderRadius: designTokens.borderRadius.button,
        ...designTokens.typography.placeholder,
        color: `${designTokens.colors.text.primary} !important`,
        '&:focus': {
            backgroundColor: 'transparent !important',
            outline: 'none !important',
        },
    },
    selectIcon: {
        color: designTokens.colors.primary,
        right: '12px',
    },
    menuPaper: {
        marginTop: '2px',
        borderRadius: designTokens.borderRadius.card,
        border: `1px solid ${designTokens.colors.border}`,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    },
    menuItem: {
        ...designTokens.typography.placeholder,
        color: designTokens.colors.text.primary,
        padding: '12px 16px',
        '&:hover': {
            backgroundColor: designTokens.colors.grey[100],
        },
        '&.Mui-selected': {
            backgroundColor: designTokens.colors.primary + '20',
            '&:hover': {
                backgroundColor: designTokens.colors.primary + '30',
            }
        }
    },
}));

const CustomTextField = withStyles({
  root: {
    'label + &': {
      marginTop: designTokens.spacing.xl,
    },
    '& input': {
        ...designTokens.typography.placeholder,
        color: designTokens.colors.text.primary,
    },
    '& input + fieldset': {
        borderRadius: designTokens.borderRadius.button,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: designTokens.colors.border,
    },
    '& input:valid:hover + fieldset': {
        borderColor: designTokens.colors.grey[300],
    },
    '& input:valid:focus + fieldset': {
        borderWidth: '1px',
        borderColor: designTokens.colors.primaryLight,
    },
    '& .Mui-error fieldset': {
        borderColor: `${designTokens.colors.red.main} !important`,
    },
    '& p.Mui-error': {
        ...designTokens.typography.error,
        marginTop: '4px',
    },
    '& .Mui-disabled fieldset': {
        borderColor: `${designTokens.colors.border} !important`,
    },
    '& .Mui-disabled input': {
        backgroundColor: `${designTokens.colors.background.input} !important`,
        color: `${designTokens.colors.text.disabled} !important`,
    },
  },
})(TextField);

const CustomInputLabel = withStyles({
  root: {
    ...designTokens.typography.body,
    transform: 'translate(0, 0px) scale(1) !important',
  },
})(InputLabel);

const CreatePatientStep = ({ phoneNumber, onCreate, loading }) => {
    const classes = useStyles();
    const [formData, setFormData] = useState({
        lastName: '',
        firstName: '',
        middleName: '',
        gender: '',
        birthDate: '',
        phone: phoneNumber,
        email: ''
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const birthDateInputRef = useRef(null);
    const [cursorPosition, setCursorPosition] = useState(null);

    useEffect(() => {
        if (cursorPosition !== null && birthDateInputRef.current) {
            birthDateInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            setCursorPosition(null);
        }
    }, [formData.birthDate, cursorPosition]);

    const formatDate = (value, previousValue, cursorPos) => {
        const numbers = value.replace(/\D/g, '');
        const prevNumbers = previousValue.replace(/\D/g, '');

        const limited = numbers.slice(0, 8);

        let formatted = '';
        if (limited.length === 0) {
            formatted = '';
        } else if (limited.length <= 2) {
            formatted = limited;
        } else if (limited.length <= 4) {
            formatted = `${limited.slice(0, 2)}.${limited.slice(2)}`;
        } else {
            formatted = `${limited.slice(0, 2)}.${limited.slice(2, 4)}.${limited.slice(4)}`;
        }

        let newCursorPos = cursorPos;

        if (numbers.length > prevNumbers.length) {
            if (formatted.length > previousValue.length + 1) {
                newCursorPos = cursorPos + 2;
            } else {
                newCursorPos = cursorPos + 1;
            }
        }
        else if (numbers.length < prevNumbers.length) {
            if (previousValue[cursorPos] === '.') {
                newCursorPos = cursorPos - 1;
            } else {
                newCursorPos = cursorPos;
            }
        }

        newCursorPos = Math.max(0, Math.min(newCursorPos, formatted.length));

        return { formatted, newCursorPos };
    };

    const validateDate = (dateString) => {
        if (!dateString) return true;

        const dateRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
        const match = dateString.match(dateRegex);

        if (!match) {
            return 'Введіть дату повністю';
        }

        const [, day, month, year] = match;
        const dayNum = parseInt(day, 10);
        const monthNum = parseInt(month, 10);
        const yearNum = parseInt(year, 10);

        const inputDate = new Date(yearNum, monthNum - 1, dayNum);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (inputDate > today) {
            return 'Введіть коректну дату';
        }

        if (monthNum < 1 || monthNum > 12) {
            return 'Некоректний місяць';
        }

        if (dayNum < 1 || dayNum > 31) {
            return 'Некоректний день';
        }

        const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
        if (dayNum > daysInMonth) {
            return 'Некоректний день';
        }

        return true;
    };

    const handleChange = (field, value, event) => {
        if (field === 'birthDate') {
            const cursorPos = event?.target?.selectionStart || 0;
            const previousValue = formData.birthDate;
            const { formatted, newCursorPos } = formatDate(value, previousValue, cursorPos);
            setFormData(prev => ({ ...prev, [field]: formatted }));
            setCursorPosition(newCursorPos);
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));

        if (field === 'birthDate' && formData.birthDate) {
            const validationResult = validateDate(formData.birthDate);
            if (validationResult !== true) {
                setErrors(prev => ({ ...prev, birthDate: validationResult }));
            }
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.lastName.trim()) {
            newErrors.lastName = "Прізвище обов'язкове";
        }
        if (!formData.firstName.trim()) {
            newErrors.firstName = "Ім'я обов'язкове";
        }
        if (!formData.gender) {
            newErrors.gender = "Стать обов'язкова";
        }

        if (formData.birthDate) {
            const dateValidation = validateDate(formData.birthDate);
            if (dateValidation !== true) {
                newErrors.birthDate = dateValidation;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validate()) {
            const dataToSubmit = { ...formData };
            if (formData.birthDate) {
                const [day, month, year] = formData.birthDate.split('.');
                dataToSubmit.birthDate = new Date(year, month - 1, day);
            } else {
                dataToSubmit.birthDate = null;
            }
            onCreate(dataToSubmit);
        }
    };

    return (
        <div className={classes.form}>
            <h3 className={classes.title}>Додати нового пацієнта</h3>

            <div className={classes.row}>
                <div className={classes.fieldWrapper}>
                    <FormControl>
                        <CustomInputLabel shrink htmlFor="lastName">
                            Прізвище*
                        </CustomInputLabel>
                        <CustomTextField
                            id="lastName"
                            placeholder="Ваше прізвище"
                            variant="outlined"
                            fullWidth
                            value={formData.lastName}
                            onChange={(e) => handleChange('lastName', e.target.value)}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                        />
                    </FormControl>
                </div>

                <div className={classes.fieldWrapper}>
                    <FormControl>
                        <CustomInputLabel shrink htmlFor="firstName">
                            Ім'я*
                        </CustomInputLabel>
                        <CustomTextField
                            id="firstName"
                            placeholder="Ваше ім'я"
                            variant="outlined"
                            fullWidth
                            value={formData.firstName}
                            onChange={(e) => handleChange('firstName', e.target.value)}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                        />
                    </FormControl>
                </div>
            </div>

            <FormControl>
                <CustomInputLabel shrink htmlFor="middleName">
                    По батькові
                </CustomInputLabel>
                <CustomTextField
                    id="middleName"
                    placeholder="Ваше ім'я по батькові"
                    variant="outlined"
                    fullWidth
                    value={formData.middleName}
                    onChange={(e) => handleChange('middleName', e.target.value)}
                />
            </FormControl>

            <div className={classes.row}>
                <div className={classes.selectWrapper}>
                    <CustomInputLabel htmlFor="gender">
                        Стать*
                    </CustomInputLabel>
                    <FormControl variant="outlined" fullWidth>
                        <Select
                            id="gender"
                            value={formData.gender}
                            onChange={(e) => handleChange('gender', e.target.value)}
                            className={classes.select}
                            error={!!errors.gender}
                            IconComponent={ExpandMoreIcon}
                            classes={{
                                select: classes.selectInput,
                                icon: classes.selectIcon,
                            }}
                            MenuProps={{
                                classes: {
                                    paper: classes.menuPaper,
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                getContentAnchorEl: null,
                            }}
                        >
                            <MenuItem value="male" className={classes.menuItem}>Чоловік</MenuItem>
                            <MenuItem value="female" className={classes.menuItem}>Жінка</MenuItem>
                        </Select>
                        {errors.gender && <span style={{ ...designTokens.typography.error, marginTop: '4px', display: 'block' }}>{errors.gender}</span>}
                    </FormControl>
                </div>

                <div className={classes.fieldWrapper}>
                    <FormControl>
                        <CustomInputLabel shrink htmlFor="birthDate">
                            Дата народження
                        </CustomInputLabel>
                        <CustomTextField
                            id="birthDate"
                            placeholder="дд.мм.рррр"
                            variant="outlined"
                            fullWidth
                            value={formData.birthDate}
                            onChange={(e) => handleChange('birthDate', e.target.value, e)}
                            onBlur={() => handleBlur('birthDate')}
                            error={!!errors.birthDate}
                            helperText={errors.birthDate}
                            inputRef={birthDateInputRef}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DateRangeRoundedIcon style={{ color: designTokens.colors.primary, fontSize: '24px', margin: '0 8px' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </FormControl>
                </div>
            </div>

            <FormControl>
                <CustomInputLabel shrink htmlFor="phone">
                    Номер телефону*
                </CustomInputLabel>
                <PhoneNumberInput
                    id="phone"
                    value={formData.phone}
                    disabled
                    style={{ marginTop: designTokens.spacing.xl }}
                />
            </FormControl>

            <FormControl>
                <CustomInputLabel shrink htmlFor="email">
                    Електронна пошта
                </CustomInputLabel>
                <CustomTextField
                    id="email"
                    placeholder="Введіть адресу пошти"
                    variant="outlined"
                    fullWidth
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon style={{ color: designTokens.colors.primary, margin: '0 8px' }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </FormControl>

            <Button
                variant="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={loading}
                fullWidth
            >
                Підтвердити пацієнта
            </Button>
        </div>
    );
};

export default CreatePatientStep;
